import { useContext } from 'react';
import { CommunityContext, useRuntimeConfig } from '../context';

interface IApiV2UploadFile {
  file: File;
  updateProgress: (percent: number) => void;
}

export const toMaxUploadMb = (maxUploadBytes: number): number => Math.round(maxUploadBytes / 1024 / 1024);

export const getMaxUploadSizeMessage = (maxUploadBytes: number): string =>
  `The maximum file size allowed is ${toMaxUploadMb(maxUploadBytes)} MB. Please upload a file within this limit.`;

export class FileTooLargeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FileTooLargeError';
  }
}

export const useMaxUploadBytes = (): number => useRuntimeConfig().maxUploadBytes;

export const useMaxUploadMb = (): number => {
  const maxUploadBytes = useMaxUploadBytes();

  return toMaxUploadMb(maxUploadBytes);
};

export const getUploadErrorMessage = (response: unknown): string | undefined => {
  const message = (response as { response?: { message?: unknown } })?.response?.message;

  return typeof message === 'string' && message.trim() !== '' ? message : undefined;
};

export const useApiV2 = () => {
  const community = useContext(CommunityContext);
  const maxUploadBytes = useMaxUploadBytes();

  const upload = async ({ file, updateProgress }: IApiV2UploadFile) => {
    if (file.size > maxUploadBytes) {
      throw new FileTooLargeError(getMaxUploadSizeMessage(maxUploadBytes));
    }

    const data = new FormData();
    data.append('file', file);
    data.append(
      'data',
      JSON.stringify({
        use_filename: true,
        hubbubId: community.id
      })
    );
    data.append('hubbubUrl', community.url);
    data.append('hubbubId', community.id);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/proxy/file');
    // Send the httpOnly user_pkey cookie cross-origin so the proxy authenticates the upload.
    xhr.withCredentials = true;
    xhr.upload.addEventListener('progress', (event) => {
      const { loaded, total } = event;
      const validLoaded = typeof loaded === 'number' && loaded >= 0 ? loaded : 0;
      const validTotal = typeof total === 'number' && total > 0 ? total : null;
      const percent = validTotal ? Math.floor((validLoaded * 100) / validTotal) : 0;
      updateProgress(percent);
    });
    xhr.send(data);
    return new Promise((resolve, reject) => {
      xhr.addEventListener('load', () => resolve(xhr.response));
      xhr.addEventListener('error', () => reject(new Error(xhr.statusText)));
    });
  };

  return [upload];
};
